import type { Component } from 'vue';
import {
  IconBranch,
  IconBulb,
  IconDocumentLock,
  IconJira,
  IconMail,
  IconSlack,
} from '@/components/design/icons';
import {
  ActionTriggerMode,
  ClauseValidationOptionType,
  PlaybookEvaluationType,
  PlaybookExtractionResponseType,
  PlaybookRiskLevel,
  TaskStatus,
  type GaiaJsonValue,
  type GroupTriggerInDb,
  type ListPlaybookEvaluationsResponse,
  type PlaybookEvaluationGroupInDb,
  type PlaybookRunInDb,
  type ReplacementSuggestion,
  type UncommonsCheckEvaluationInDb,
  type UncommonsFinding,
} from '@/api';
import { isEnumValue } from '@/utils/enumUtils';
import { initialsOf } from '@/utils/initials';

export type TagType = 'default' | 'success' | 'info' | 'warning' | 'error';

export type PlaybookEvaluation = ListPlaybookEvaluationsResponse[number];
export type PlaybookEvaluationOption = NonNullable<PlaybookEvaluation['options']>[number];
export type PlaybookEvaluationResult = NonNullable<PlaybookRunInDb['evaluation_results']>[number];
export type PlaybookActionResult = NonNullable<PlaybookEvaluationResult['action_results']>[number];
export type PlaybookConfiguredAction = NonNullable<PlaybookEvaluationOption['actions']>[number];
export type PlaybookFindingThresholdTrigger = NonNullable<
  UncommonsCheckEvaluationInDb['finding_threshold_triggers']
>[number];

export type { ReplacementSuggestion } from '@/api';

export type SeverityBucket = 'High' | 'Medium' | 'Low' | 'Other';
export const SEVERITY_ORDER: SeverityBucket[] = ['High', 'Medium', 'Low', 'Other'];

/** True when a value has the shape of a ReplacementSuggestion redline. */
function isReplacementSuggestion(value: unknown): value is ReplacementSuggestion {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.string_to_replace === 'string' &&
    typeof candidate.replacement_string === 'string'
  );
}

/**
 * Parse a suggestion action result's `suggestion` string into redlines. The
 * backend serialises a list of `ReplacementSuggestion` as JSON; older/edge data
 * may be a single object or plain prose. Returns [] when it isn't valid redline
 * JSON so callers can fall back to showing the raw text.
 */
export function parseSuggestionRedlines(
  suggestion: string | null | undefined
): ReplacementSuggestion[] {
  if (!suggestion?.trim()) return [];
  try {
    const parsed: unknown = JSON.parse(suggestion);
    const list = Array.isArray(parsed) ? parsed : [parsed];
    return list.filter(isReplacementSuggestion);
  } catch {
    return [];
  }
}

export type RedlineSegmentKind = 'unchanged' | 'removed' | 'added';
export type RedlineSegment = { kind: RedlineSegmentKind; text: string };

/**
 * Diff `before` against `after` into unchanged / removed / added segments by
 * sharing the common prefix and suffix. Keeps insertions readable (most of the
 * text stays `unchanged` and only the appended part is `added`), mirroring the
 * inline preview used in the Word add-in.
 */
export function buildRedlineSegments(before: string, after: string): RedlineSegment[] {
  if (before === after) {
    return after.length ? [{ kind: 'unchanged', text: after }] : [];
  }

  let prefixLength = 0;
  while (
    prefixLength < before.length &&
    prefixLength < after.length &&
    before[prefixLength] === after[prefixLength]
  ) {
    prefixLength += 1;
  }

  let suffixLength = 0;
  while (
    suffixLength < before.length - prefixLength &&
    suffixLength < after.length - prefixLength &&
    before[before.length - 1 - suffixLength] === after[after.length - 1 - suffixLength]
  ) {
    suffixLength += 1;
  }

  const removed = before.slice(
    prefixLength,
    suffixLength ? before.length - suffixLength : before.length
  );
  const added = after.slice(
    prefixLength,
    suffixLength ? after.length - suffixLength : after.length
  );

  return (
    [
      { kind: 'unchanged', text: before.slice(0, prefixLength) },
      { kind: 'removed', text: removed },
      { kind: 'added', text: added },
      {
        kind: 'unchanged',
        text: suffixLength ? before.slice(before.length - suffixLength) : '',
      },
    ] as RedlineSegment[]
  ).filter(segment => segment.text.length > 0);
}

/** Sort rank for a severity bucket (lower = more severe); unknown buckets sort last. */
export function severityBucketRank(bucket: SeverityBucket): number {
  const index = SEVERITY_ORDER.indexOf(bucket);
  return index === -1 ? SEVERITY_ORDER.length : index;
}

/** Display order for evaluation-type grouping. */
export const EVALUATION_TYPE_ORDER: PlaybookEvaluationType[] = [
  PlaybookEvaluationType.CLASSIFICATION,
  PlaybookEvaluationType.CLAUSE_VALIDATION,
  PlaybookEvaluationType.UNCOMMONS_CHECK,
  PlaybookEvaluationType.EXTRACTION,
];

const evaluationTypeLabelMap: Record<PlaybookEvaluationType, string> = {
  [PlaybookEvaluationType.CLASSIFICATION]: 'Classification',
  [PlaybookEvaluationType.CLAUSE_VALIDATION]: 'Clause validation',
  [PlaybookEvaluationType.UNCOMMONS_CHECK]: 'Uncommons',
  [PlaybookEvaluationType.EXTRACTION]: 'Extraction',
};

/** Human-readable label for an evaluation type; falls back to a title-cased raw value. */
export function evaluationTypeLabel(type: string | null | undefined): string {
  if (isEnumValue(PlaybookEvaluationType, type)) {
    return evaluationTypeLabelMap[type];
  }
  if (!type) return 'Other';
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const clauseOptionTypeLabelMap: Record<ClauseValidationOptionType, string> = {
  [ClauseValidationOptionType.PREFERRED]: 'Preferred position',
  [ClauseValidationOptionType.FALLBACK]: 'Acceptable fallback',
  [ClauseValidationOptionType.ESCALATION]: 'Escalation required when',
};

/** Descriptive label for a clause-validation option tier, used as section headers in the wizard. */
export function clauseOptionTypeLabel(type: ClauseValidationOptionType): string {
  return clauseOptionTypeLabelMap[type];
}

const clauseOptionTypeShortLabelMap: Record<ClauseValidationOptionType, string> = {
  [ClauseValidationOptionType.PREFERRED]: 'Preferred',
  [ClauseValidationOptionType.FALLBACK]: 'Fallback',
  [ClauseValidationOptionType.ESCALATION]: 'Escalation',
};

/** Short label for a clause-validation option tier, used for compact tags. */
export function clauseOptionTypeShortLabel(type: ClauseValidationOptionType): string {
  return clauseOptionTypeShortLabelMap[type];
}

const riskTagTypeMap: Record<PlaybookRiskLevel, TagType> = {
  [PlaybookRiskLevel.NONE]: 'default',
  [PlaybookRiskLevel.LOW]: 'success',
  [PlaybookRiskLevel.MEDIUM]: 'warning',
  [PlaybookRiskLevel.HIGH]: 'error',
};

export function riskTagType(level: PlaybookRiskLevel | null | undefined): TagType {
  return level ? riskTagTypeMap[level] : 'default';
}

const stepStatusTagTypeMap: Record<TaskStatus, TagType> = {
  [TaskStatus.DONE]: 'success',
  [TaskStatus.RUNNING]: 'info',
  [TaskStatus.FAILED]: 'error',
  [TaskStatus.SKIPPED]: 'warning',
  [TaskStatus.PENDING]: 'default',
  [TaskStatus.PARTIAL]: 'warning',
  [TaskStatus.CANCELLED]: 'default',
  [TaskStatus.WARNING]: 'warning',
};

export function stepStatusTagType(status: TaskStatus): TagType {
  return stepStatusTagTypeMap[status];
}

const severityTagTypeMap: Record<SeverityBucket, TagType> = {
  High: 'error',
  Medium: 'warning',
  Low: 'success',
  Other: 'default',
};

export function severityTagType(bucket: SeverityBucket): TagType {
  return severityTagTypeMap[bucket];
}

export function severityRank(level: PlaybookRiskLevel | null | undefined): number {
  switch (level) {
    case PlaybookRiskLevel.HIGH:
      return 3;
    case PlaybookRiskLevel.MEDIUM:
      return 2;
    case PlaybookRiskLevel.LOW:
      return 1;
    default:
      return 0;
  }
}

/** option uid → its definition, across every evaluation in the playbook. */
export function buildOptionMap(
  evaluations: ListPlaybookEvaluationsResponse
): Map<string, PlaybookEvaluationOption> {
  const map = new Map<string, PlaybookEvaluationOption>();
  for (const evaluation of evaluations) {
    for (const option of evaluation.options ?? []) {
      map.set(option.uid, option);
    }
  }
  return map;
}

/** A run's raw input as one text blob — the backend stores it as one entry per page. */
export function runInputText(run: Pick<PlaybookRunInDb, 'raw_input'>): string | null {
  const pages = run.raw_input;
  return pages?.length ? pages.join('\n\n') : null;
}

/**
 * Who kicked a run off. A run started from the platform carries the user, one
 * started outside it only the address it came in on, so the two fields stand in
 * for each other and whichever is set is the answer. Empty when neither is.
 */
export function startedByLabel(
  run: Pick<PlaybookRunInDb, 'started_by_user' | 'started_by_email'>
): string {
  return (
    [run.started_by_user?.full_name, run.started_by_user?.email, run.started_by_email].find(
      Boolean
    ) ?? ''
  );
}

/**
 * The initials on that person's avatar. An address goes in without its domain —
 * every address at the org shares one, so "gaia.law" would initial half the
 * table the same way. `initialsOf` splits on dots, which is what makes the
 * remaining local part ("janina.moellmann") read as a name.
 */
export function startedByInitials(
  run: Pick<PlaybookRunInDb, 'started_by_user' | 'started_by_email'>
): string {
  const name = run.started_by_user?.full_name.trim();
  if (name) return initialsOf(name);
  const address = [run.started_by_user?.email, run.started_by_email].find(Boolean) ?? '';
  return initialsOf(address.split('@')[0]);
}

/** Pick the latest result for an evaluation within a run (by is_latest, then attempt). */
export function latestResultForEvaluation(
  run: PlaybookRunInDb,
  evaluationUid: string
): PlaybookEvaluationResult | null {
  const all = (run.evaluation_results ?? []).filter(
    result => result.evaluation_uid === evaluationUid
  );
  if (all.length === 0) return null;
  const latest = all.filter(result => result.is_latest);
  const pool = latest.length > 0 ? latest : all;
  return pool.reduce((best, current) =>
    current.attempt_number > best.attempt_number ? current : best
  );
}

/** Map a finding's own severity onto the shared severity bucket vocabulary. */
export function findingSeverityBucket(
  severity: UncommonsFinding['severity'] | null | undefined
): SeverityBucket {
  switch (severity) {
    case 'high':
      return 'High';
    case 'medium':
      return 'Medium';
    case 'low':
      return 'Low';
    default:
      return 'Other';
  }
}

/** True when a result belongs to an uncommons-check evaluation (its result is a list). */
export function isUncommonsResult(result: PlaybookEvaluationResult): boolean {
  return result.evaluation_type === 'uncommons_check';
}

/** True when a result belongs to an extraction evaluation (it carries an extracted value). */
export function isExtractionResult(result: PlaybookEvaluationResult): boolean {
  return result.evaluation_type === 'extraction';
}

/** The extracted value of an extraction result; null for any other result type. */
export function extractedValue(result: PlaybookEvaluationResult): GaiaJsonValue {
  return isExtractionResult(result) && 'extracted_value' in result
    ? (result.extracted_value ?? null)
    : null;
}

/** The configured response type of an extraction evaluation; null for any other type. */
export function extractionResponseType(
  evaluation: PlaybookEvaluation
): PlaybookExtractionResponseType | null {
  return evaluation.evaluation_type === 'extraction' && 'response_type' in evaluation
    ? evaluation.response_type
    : null;
}

/**
 * Selectable response types for an extraction evaluation, labelled to match the review
 * extraction agent (see `AgentEditDrawer.vue`). Every `PlaybookExtractionResponseType` value
 * is covered so nothing the backend accepts is unselectable.
 */
export const extractionTypeOptions: {
  label: string;
  value: PlaybookExtractionResponseType;
}[] = [
  { label: 'Text', value: PlaybookExtractionResponseType.STR },
  { label: 'List of Text', value: PlaybookExtractionResponseType.LIST_STR },
  { label: 'Whole number', value: PlaybookExtractionResponseType.INT },
  { label: 'List of whole numbers', value: PlaybookExtractionResponseType.LIST_INT },
  { label: 'Number', value: PlaybookExtractionResponseType.FLOAT },
  { label: 'List of Numbers', value: PlaybookExtractionResponseType.LIST_FLOAT },
  { label: 'Yes/No', value: PlaybookExtractionResponseType.BOOL },
  { label: 'List of Yes/No', value: PlaybookExtractionResponseType.LIST_BOOL },
  { label: 'Date', value: PlaybookExtractionResponseType.DATE },
  { label: 'List of Dates', value: PlaybookExtractionResponseType.LIST_DATE },
  { label: 'Date & Time', value: PlaybookExtractionResponseType.DATETIME },
  { label: 'List of Date & Time', value: PlaybookExtractionResponseType.LIST_DATETIME },
];

const extractionTypeLabelMap = new Map<string, string>(
  extractionTypeOptions.map(option => [option.value, option.label])
);

/** Human-readable label for an extraction response type; falls back to the raw value. */
export function extractionTypeLabel(type: string | null | undefined): string {
  if (!type) return 'Value';
  return extractionTypeLabelMap.get(type) ?? type;
}

/**
 * The uid of the signing request a signature-preparation action created; null when the
 * action is of another type or hasn't produced a request (yet or because it failed).
 */
export function signaturePreparationRequestUid(
  result: PlaybookActionResult | null | undefined
): string | null {
  if (!result || result.action_type !== 'signature_preparation') return null;
  return result.signing_request_uid ?? null;
}

/**
 * The group each group trigger belongs to, keyed by trigger uid.
 *
 * A group trigger fires for a whole group, but its actions are recorded like any
 * other action result — on whichever member evaluation happened to run them. This
 * map is the only way to tell them apart from an evaluation's own actions, so every
 * surface that shows group actions (or hides them from evaluation cells) shares it.
 */
export function groupsByTriggerUid(
  groups: PlaybookEvaluationGroupInDb[]
): Map<string, PlaybookEvaluationGroupInDb> {
  const byTriggerUid = new Map<string, PlaybookEvaluationGroupInDb>();
  for (const group of groups) {
    for (const trigger of group.triggers ?? []) byTriggerUid.set(trigger.uid, group);
  }
  return byTriggerUid;
}

/** Display name of an evaluation group, which may be unnamed. */
export function groupDisplayName(group: PlaybookEvaluationGroupInDb): string {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
  return group.name?.trim() || 'Untitled group';
}

/** The defining detail of an action that ran — recipient, request, or suggestion text. */
export function actionResultDetail(result: PlaybookActionResult): string {
  if (result.action_type === 'notification') {
    return result.recipient_email ? `To: ${result.recipient_email}` : '';
  }
  if (result.action_type === 'signature_preparation') {
    return result.signing_request_uid ? 'Signature request created' : '';
  }
  if (result.action_type === 'slack_notification') {
    // "done" means queued: the aggregated Slack message goes out once the run finishes.
    if (result.status === TaskStatus.DONE) {
      return "Included in the run's Slack summary, posted when the run finishes.";
    }
    if (result.status === TaskStatus.FAILED) {
      return result.error?.trim() ?? '';
    }
    return '';
  }
  if (result.action_type === 'jira_notification') {
    // "done" means queued: the one comment per run goes up once the run finishes.
    if (result.status === TaskStatus.DONE) {
      return "Included in the run's Jira comment, posted when the run finishes.";
    }
    if (result.status === TaskStatus.FAILED) {
      // Where "this run has no source Jira issue" surfaces.
      return result.error?.trim() ?? '';
    }
    return '';
  }
  if (result.action_type === 'suggestion') {
    const redlines = parseSuggestionRedlines(result.suggestion);
    if (redlines.length) {
      return redlines.length === 1 ? '1 suggested edit' : `${redlines.length} suggested edits`;
    }
    const text = result.suggestion?.trim();
    if (!text) return '';
    return text.length > 60 ? `${text.slice(0, 60)}…` : text;
  }
  return '';
}

/** Past-tense label for an action that ran; falls back to the suggestion wording. */
export function actionResultTypeLabel(actionType: PlaybookActionResult['action_type']): string {
  if (actionType === 'notification') return 'Email sent';
  if (actionType === 'evaluation') return 'Follow-up evaluation';
  if (actionType === 'signature_preparation') return 'Signature prepared';
  if (actionType === 'slack_notification') return 'Slack notification';
  if (actionType === 'jira_notification') return 'Jira notification';
  return 'Suggestion';
}

/** Icon component for an action type; falls back to the suggestion (bulb) icon. */
export function actionTypeIcon(actionType: PlaybookActionResult['action_type']): Component {
  if (actionType === 'notification') return IconMail;
  if (actionType === 'evaluation') return IconBranch;
  if (actionType === 'signature_preparation') return IconDocumentLock;
  if (actionType === 'slack_notification') return IconSlack;
  if (actionType === 'jira_notification') return IconJira;
  return IconBulb;
}

// Left-to-right icon order, so the same action always sits in the same place
// instead of following whatever order the results came back in.
const ACTION_TYPE_ORDER: PlaybookActionResult['action_type'][] = [
  'signature_preparation',
  'notification',
  'slack_notification',
  'jira_notification',
  'suggestion',
  'evaluation',
];

export function actionTypeRank(actionType: PlaybookActionResult['action_type']): number {
  const index = ACTION_TYPE_ORDER.indexOf(actionType);
  // Unknown types sort last rather than jumping to the front.
  return index === -1 ? ACTION_TYPE_ORDER.length : index;
}

export type FiredGroupAction = {
  result: PlaybookActionResult;
  group: PlaybookEvaluationGroupInDb;
};

/** Every group-triggered action that ran on this run, with the group that fired it. */
export function firedGroupActions(
  run: PlaybookRunInDb,
  groups: PlaybookEvaluationGroupInDb[]
): FiredGroupAction[] {
  const byTriggerUid = groupsByTriggerUid(groups);
  if (byTriggerUid.size === 0) return [];
  return (run.evaluation_results ?? []).flatMap(evaluationResult =>
    (evaluationResult.action_results ?? [])
      .filter(result => result.is_latest)
      .flatMap(result => {
        const group = byTriggerUid.get(result.action_trigger_uid);
        return group ? [{ result, group }] : [];
      })
  );
}

/**
 * Every action configured on an evaluation: those hanging off its options, plus
 * an uncommons check's finding-threshold triggers. Empty means the evaluation
 * can never fire anything, which is what tells "nothing fired" apart from
 * "nothing to fire".
 */
export function evaluationConfiguredActions(
  evaluation: PlaybookEvaluation
): PlaybookConfiguredAction[] {
  const fromOptions = (evaluation.options ?? []).flatMap(option => option.actions ?? []);
  const fromThresholds = findingThresholdTriggers(evaluation).flatMap(
    trigger => trigger.actions ?? []
  );
  return [...fromOptions, ...fromThresholds];
}

/**
 * The actions one evaluation result fired, in the icon order the grid uses.
 * Group-triggered actions are left out: they belong to the group, not to
 * whichever member evaluation happened to record them, and have their own
 * column. Pass the group trigger uids from `groupsByTriggerUid`.
 */
export function firedEvaluationActions(
  result: PlaybookEvaluationResult,
  groupTriggerUids: Set<string>
): PlaybookActionResult[] {
  return (result.action_results ?? [])
    .filter(action => action.is_latest && !groupTriggerUids.has(action.action_trigger_uid))
    .sort((a, b) => actionTypeRank(a.action_type) - actionTypeRank(b.action_type));
}

/** The actions configured across all of a group's triggers. */
export function groupConfiguredActions(
  group: PlaybookEvaluationGroupInDb
): PlaybookConfiguredAction[] {
  return (group.triggers ?? []).flatMap(trigger => trigger.actions ?? []);
}

/** The findings of an uncommons-check result; empty for any other result type. */
export function uncommonsFindings(result: PlaybookEvaluationResult): UncommonsFinding[] {
  return isUncommonsResult(result) && 'findings' in result ? (result.findings ?? []) : [];
}

/** The finding-threshold triggers configured on an uncommons-check evaluation; empty otherwise. */
export function findingThresholdTriggers(
  evaluation: PlaybookEvaluation
): PlaybookFindingThresholdTrigger[] {
  return 'finding_threshold_triggers' in evaluation
    ? (evaluation.finding_threshold_triggers ?? [])
    : [];
}

/** Human-readable summary of a finding-threshold trigger's rule. */
export function formatFindingThresholdRule(trigger: PlaybookFindingThresholdTrigger): string {
  if (trigger.mode === ActionTriggerMode.ALWAYS) {
    return 'Always';
  }
  if (trigger.mode === ActionTriggerMode.NONE_FOUND) {
    return 'When no findings are detected';
  }
  const count = trigger.min_count;
  const noun = count === 1 ? 'finding' : 'findings';
  if (trigger.mode === ActionTriggerMode.LESS_THAN) {
    return `When < ${count} ${trigger.min_severity}-severity ${noun}`;
  }
  return `When ≥ ${count} ${trigger.min_severity}-severity ${noun}`;
}

/** Human-readable summary of a group trigger's rule. */
export function formatGroupTriggerRule(
  trigger: Pick<GroupTriggerInDb, 'mode' | 'min_risk_level' | 'min_count'>
): string {
  if (trigger.mode === ActionTriggerMode.ALWAYS) {
    return 'Always';
  }
  const risk = trigger.min_risk_level;
  if (trigger.mode === ActionTriggerMode.NONE_FOUND) {
    return `No results at ${risk} risk or higher`;
  }
  const count = trigger.min_count;
  const noun = count === 1 ? 'result' : 'results';
  if (trigger.mode === ActionTriggerMode.LESS_THAN) {
    return `Fewer than ${count} ${noun} at ${risk} risk or higher`;
  }
  return `At least ${count} ${noun} at ${risk} risk or higher`;
}

function findingSeverityRank(severity: UncommonsFinding['severity'] | null | undefined): number {
  switch (severity) {
    case 'high':
      return 3;
    case 'medium':
      return 2;
    case 'low':
      return 1;
    default:
      return 0;
  }
}

/** Average severity across findings, rounded to a single bucket; Other when empty. */
export function averageFindingBucket(findings: UncommonsFinding[]): SeverityBucket {
  if (findings.length === 0) return 'Other';
  const total = findings.reduce((sum, finding) => sum + findingSeverityRank(finding.severity), 0);
  const average = Math.round(total / findings.length);
  if (average >= 3) return 'High';
  if (average === 2) return 'Medium';
  if (average === 1) return 'Low';
  return 'Other';
}

/** Severity bucket = highest risk level among matched options; Other when none. */
export function resultSeverity(
  result: PlaybookEvaluationResult,
  optionMap: Map<string, PlaybookEvaluationOption>
): SeverityBucket {
  let best = 0;
  for (const uid of result.matched_option_uids ?? []) {
    best = Math.max(best, severityRank(optionMap.get(uid)?.risk_level ?? null));
  }
  if (best === 3) return 'High';
  if (best === 2) return 'Medium';
  if (best === 1) return 'Low';
  return 'Other';
}
